import { useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { useConfig } from '../context/ConfigContext';
import { getCurrencySymbol } from '../utils/currencyUtil';
import { normalisePosCustomization } from '../utils/posCustomizationUtil';
import {
  POS_CUSTOMIZATION_TYPES,
  POS_CUSTOMIZATION_TYPE_CHOICES,
  POS_CUSTOMIZATION_CHOICES_MAX,
  POS_CUSTOMIZATION_LABEL_MAX_LENGTH,
} from '../constants/posConst';
import '../css/Pos.css';

/**
 * A blank choice row. `key` is only for React — it is not saved — so rows can
 * be removed from the middle without the inputs below swapping their text.
 *
 * @returns {{key: string, name: string, price: string}}
 */
const blankChoice = () => ({ key: `c_${Math.random().toString(36).slice(2, 9)}`, name: '', price: '' });

/**
 * Adds or edits ONE customization option of a product — "Sugar level",
 * "Add-ons", "Name on cup".
 *
 * NOTHING IS WRITTEN FROM HERE. The option is handed back to the product form,
 * and saved with the rest of the product when that form is saved — so
 * cancelling the product form really does leave the product as it was, options
 * included.
 *
 * Opens on top of the product form, hence the stacked overlay. Closes on the
 * × or Cancel only (CLAUDE.md > Behavior): a half-typed list of choices must
 * not vanish because a tap landed on the backdrop.
 *
 * @param {object} props
 * @param {object|null} props.option - the option being edited, or null to add one
 * @param {Array<{id: string, label: string}>} props.siblings - the product's
 *   other options, so two cannot be given the same label
 * @param {(option: object) => void} props.onSave - receives the cleaned option
 * @param {() => void} props.onClose
 * @returns {JSX.Element}
 */
function PosCustomizationModal({ option, siblings, onSave, onClose }) {
  const { config } = useConfig();
  const [label, setLabel] = useState(option?.label || '');
  const [type, setType] = useState(option?.type || POS_CUSTOMIZATION_TYPES.CHOICE);
  const [required, setRequired] = useState(Boolean(option?.required));
  const [multiple, setMultiple] = useState(Boolean(option?.multiple));
  // Always at least one row, so there is somewhere to start typing.
  const [choices, setChoices] = useState(() => {
    const existing = (option?.choices || []).map((choice) => ({
      ...blankChoice(),
      name: choice.name,
      price: choice.price ? String(choice.price) : '',
    }));
    return existing.length > 0 ? existing : [blankChoice()];
  });
  const [error, setError] = useState('');

  const isChoice = type === POS_CUSTOMIZATION_TYPES.CHOICE;
  const atChoiceLimit = choices.length >= POS_CUSTOMIZATION_CHOICES_MAX;

  /**
   * Changes one field of one choice row.
   *
   * @param {string} key - the row's React key
   * @param {'name'|'price'} field
   * @param {string} value
   * @returns {void}
   */
  const updateChoice = (key, field, value) => {
    setChoices((rows) => rows.map((row) => (row.key === key ? { ...row, [field]: value } : row)));
  };

  /**
   * Removes a choice row, keeping one blank row when the last is removed.
   *
   * @param {string} key
   * @returns {void}
   */
  const removeChoice = (key) => {
    setChoices((rows) => {
      const next = rows.filter((row) => row.key !== key);
      return next.length > 0 ? next : [blankChoice()];
    });
  };

  const addChoice = () => {
    if (atChoiceLimit) return;
    setChoices((rows) => [...rows, blankChoice()]);
  };

  /**
   * Enter in a choice's name adds the next row — typing a list of sugar
   * levels should not need the mouse between each one.
   *
   * @param {React.KeyboardEvent<HTMLInputElement>} event
   * @param {number} index - the row's position
   * @returns {void}
   */
  const handleChoiceKey = (event, index) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    if (index === choices.length - 1 && String(choices[index].name).trim()) addChoice();
  };

  const handleSave = () => {
    setError('');
    try {
      const cleaned = normalisePosCustomization({
        id: option?.id,
        label,
        type,
        required,
        multiple,
        choices: choices.map(({ name, price }) => ({ name, price })),
      }, siblings);
      onSave(cleaned);
    } catch (err) {
      setError(err.message || 'This option could not be saved.');
    }
  };

  return createPortal(
    <div className="pos-modal-overlay pos-modal-overlay-stacked">
      <div className="pos-modal" role="dialog" aria-modal="true" aria-label={option ? 'Edit option' : 'Add option'}>
        <header className="pos-modal-head">
          <h3>{option ? 'Edit option' : 'Add option'}</h3>
          <button type="button" className="pos-modal-close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </header>

        <div className="pos-modal-body">
          <label className="pos-field">
            <span>Label</span>
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Sugar level"
              maxLength={POS_CUSTOMIZATION_LABEL_MAX_LENGTH}
              autoFocus
            />
            <span className="pos-theme-hint">What staff are asked at the counter, and what the receipt prints.</span>
          </label>

          <fieldset className="pos-opt-types">
            <legend>How is it answered?</legend>
            <div className="pos-opt-type-grid">
              {POS_CUSTOMIZATION_TYPE_CHOICES.map((choice) => (
                <label key={choice.key} className={`pos-opt-type${type === choice.key ? ' active' : ''}`}>
                  <input
                    type="radio"
                    name="pos-opt-type"
                    value={choice.key}
                    checked={type === choice.key}
                    onChange={() => setType(choice.key)}
                  />
                  <span>
                    <strong>{choice.label}</strong>
                    <em>{choice.description}</em>
                  </span>
                </label>
              ))}
            </div>
          </fieldset>

          {isChoice && (
            <div className="pos-field">
              <span>Choices</span>
              <div className="pos-opt-choice-head" aria-hidden="true">
                <span>Name</span>
                <span>Extra price ({getCurrencySymbol(config)})</span>
                <span />
              </div>
              <ul className="pos-opt-choices">
                {choices.map((row, index) => (
                  <li key={row.key} className="pos-opt-choice">
                    <input
                      type="text"
                      value={row.name}
                      onChange={(e) => updateChoice(row.key, 'name', e.target.value)}
                      onKeyDown={(e) => handleChoiceKey(e, index)}
                      placeholder={index === 0 ? '50%' : 'Another choice'}
                      maxLength={POS_CUSTOMIZATION_LABEL_MAX_LENGTH}
                      aria-label={`Choice ${index + 1} name`}
                    />
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      inputMode="decimal"
                      value={row.price}
                      onChange={(e) => updateChoice(row.key, 'price', e.target.value)}
                      placeholder="0"
                      aria-label={`Choice ${index + 1} extra price`}
                    />
                    <button
                      type="button"
                      className="pos-opt-remove"
                      onClick={() => removeChoice(row.key)}
                      aria-label={`Remove choice ${index + 1}`}
                    >
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
              <button type="button" className="pos-stock-add pos-opt-add" onClick={addChoice} disabled={atChoiceLimit}>
                Add choice
              </button>
              <span className="pos-theme-hint">
                Leave the extra price blank when a choice costs nothing more — a sugar level, say.
                Add-ons like pearls can carry their own price.
              </span>
            </div>
          )}

          {isChoice && (
            <label className="pos-check">
              <input type="checkbox" checked={multiple} onChange={(e) => setMultiple(e.target.checked)} />
              <span>
                <strong>Can pick more than one</strong>
                <em>For add-ons — pearl and coffee jelly in the same cup</em>
              </span>
            </label>
          )}

          <label className="pos-check">
            <input type="checkbox" checked={required} onChange={(e) => setRequired(e.target.checked)} />
            <span>
              <strong>Required</strong>
              <em>Staff must answer this before the item can be added to an order</em>
            </span>
          </label>

          {error && <p className="pos-error">{error}</p>}
        </div>

        <footer className="pos-modal-foot">
          <span className="pos-foot-spacer" />
          <button type="button" className="pos-btn pos-btn-ghost" onClick={onClose}>Cancel</button>
          <button type="button" className="pos-btn pos-btn-primary" onClick={handleSave}>
            {option ? 'Save option' : 'Add option'}
          </button>
        </footer>
      </div>
    </div>,
    document.body,
  );
}

export default PosCustomizationModal;
